import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

export type ConversationStatus = 'OPEN' | 'CLOSED' | 'SNOOZED';
export type MessageDirection = 'OUTBOUND' | 'INBOUND';

export interface InboxContact {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  company: string | null;
}

export type ConversationChannel = 'EMAIL' | 'SMS';

export interface ConversationListItem {
  id: string;
  org_id: string;
  contact_ref_id: string;
  contact_type: string;
  recruiter_user_id: string | null;
  campaign_id: string | null;
  last_message_at: string | null;
  last_read_at: string | null;
  snoozed_until: string | null;
  status: ConversationStatus;
  unread: boolean;
  contact: InboxContact | null;
  preview: string | null;
  subject: string | null;
  channel: ConversationChannel;
  contact_handle: string | null;
}

export interface ConversationMessage {
  id: string;
  direction: MessageDirection;
  subject: string | null;
  body_html: string | null;
  body_text: string | null;
  from_address: string | null;
  from_phone: string | null;
  to_phone: string | null;
  sent_at: string | null;
  created_at: string;
}

export interface ConversationDetail {
  id: string;
  org_id: string;
  contact_ref_id: string;
  contact_type: string;
  recruiter_user_id: string | null;
  campaign: { id: string; name: string; type: string } | null;
  last_message_at: string | null;
  last_read_at: string | null;
  snoozed_until: string | null;
  status: ConversationStatus;
  unread: boolean;
  contact: InboxContact | null;
  channel: ConversationChannel;
  contact_handle: string | null;
  /** SMS only: the person opted out of texts, so replying is blocked. */
  sms_opted_out?: boolean;
  messages: ConversationMessage[];
}

export interface ConversationFilters {
  status?: ConversationStatus;
  campaign_id?: string;
  recruiter_id?: string;
  unread?: boolean;
}

export function useConversations(filters: ConversationFilters = {}) {
  return useQuery<{ data: ConversationListItem[] }>({
    queryKey: ['inbox', 'conversations', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ConversationListItem[] }>('/inbox/conversations', {
        params: filters,
      });
      return data;
    },
    // Poll every 30s so new replies surface without a manual refresh.
    refetchInterval: 30_000,
  });
}

export function useConversation(id: string | null | undefined) {
  return useQuery<ConversationDetail>({
    queryKey: ['inbox', 'conversation', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ConversationDetail }>(`/inbox/conversations/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

/** Email replies send subject + body_html; text replies send body_text only (JT1-1859). */
export interface ReplyPayload {
  subject?: string;
  body_html?: string;
  body_text?: string;
}

export function useReply(conversationId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ReplyPayload) => {
      const { data } = await apiClient.post<{ data: ConversationDetail }>(
        `/inbox/conversations/${conversationId}/reply`,
        payload
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['inbox', 'conversation', conversationId] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await apiClient.post<{ data: ConversationDetail }>(
        `/inbox/conversations/${conversationId}/read`
      );
      return data.data;
    },
    onSuccess: (_data, conversationId) => {
      qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['inbox', 'conversation', conversationId] });
    },
  });
}

export function useMarkUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await apiClient.post<{ data: ConversationDetail }>(
        `/inbox/conversations/${conversationId}/unread`
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export function useSnooze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { conversationId: string; until: string | null }) => {
      const { data } = await apiClient.post<{ data: ConversationDetail }>(
        `/inbox/conversations/${args.conversationId}/snooze`,
        { snoozed_until: args.until }
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export function useCloseConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { data } = await apiClient.post<{ data: ConversationDetail }>(
        `/inbox/conversations/${conversationId}/close`
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
}
